<template>
	<div :class="$style['tab-wrapper']">
		<FormSection :title="$t('common.fields.personal-information')">
			<BaseTextField
				v-model="nickname"
				:disabled="disabled"
				:helper-text="errors.first('nickname')"
				:invalid="errors.has('nickname')"
				name="nickname"
				type="text"
				:label-text="`${$t('common.fields.nickname')} (${$t('common.messages.not-required')})`"
			/>
			<InputGroup>
				<BaseTextField
					v-model="name"
					v-validate="locked ? '' : 'required'"
					:disabled="disabled"
					:helper-text="errors.first('name')"
					:invalid="errors.has('name')"
					name="name"
					type="text"
					multiple
					:label-text="$t('common.fields.firstname')"
				/>
				<BaseTextField
					v-model="surname"
					v-validate="locked ? '' : 'required'"
					:disabled="disabled"
					:helper-text="errors.first('surname')"
					:invalid="errors.has('surname')"
					name="surname"
					type="text"
					multiple
					:label-text="$t('common.fields.surname')"
				/>
			</InputGroup>
		</FormSection>
		<FormSection :title="$t('common.fields.address-information')">
			<AutoCompleteField
				v-model="address"
				:disabled="disabled"
				name="address"
				:label-text="$t('common.fields.address')"
				@place-changed="handlePlaceChange"
			/>
			<BaseDropdownInput
				v-model="country"
				:disabled="disabled"
				:list="countries"
				name="country"
				:label-text="$t('common.fields.country')"
			/>
			<InputGroup>
				<BaseTextField
					v-model="postalCode"
					:disabled="disabled"
					name="postal-code"
					multiple="true"
					type="text"
					:label-text="$t('common.fields.postal-code')"
				/>
				<AutoCompleteField
					v-model="city"
					:disabled="disabled"
					name="city"
					types="(regions)"
					multiple="true"
					:label-text="$t('common.fields.city')"
					:country="country.id || null"
				/>
			</InputGroup>
		</FormSection>
		<FormSection :title="$t('common.fields.contact-information')">
			<BaseTextField
				v-model="email"
				:disabled="disabled"
				name="email"
				type="email"
				:label-text="$t('common.fields.email')"
			/>
			<PhoneNumberField
				ref="phoneNumber"
				v-model="phoneNumber"
				:disabled="disabled"
				:required="!locked"
				:default-country="country?.id || defaultCountry"
				:label-text="$t('common.fields.phone')"
				:helper-text="errors.first('phoneNumber')"
				:invalid="errors.has('phoneNumber')"
				@valid="errors.remove('phoneNumber')"
			/>
		</FormSection>
	</div>
</template>

<script>

import { mapMutations, mapGetters } from '@/store/modules/Clients/helpers'
import { UPDATE_CLIENT_FORM } from '@/store/modules/Clients/mutation-types'
import AutoCompleteField from '@/components/Essentials/AutoCompleteField'
import FormSection from '@/components/Essentials/FormSection'
import PhoneNumberField from '@/components/Essentials/PhoneNumberField'
import { getCityFromPlace, getCountryComponentFromPlace } from '@/utils/address'
export default {
	name: 'ClientInfo',
	components: {
		AutoCompleteField,
		FormSection,
		PhoneNumberField
	},
	props: {
		countries: {
			type: Array,
			default: () => []
		},
		disabled: {
			type: Boolean,
			default: false
		},
		locked: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			country: {
				name: null,
				id: null
			},
			localNickname: null,
			localName: null,
			localSurname: null,
			localEmail: null,
			localPhoneNumber: null,
			localCity: null,
			localAddress: null,
			localPostalCode: null
		}
	},
	computed: {

		...mapGetters({
			editClientForm: 'editClientForm'
		}),

		defaultCountry () {
			if (this.$i18n.locale === 'en') {
				return 'gb'
			}
			return this.$i18n.locale
		},

		nickname: {
			get () {
				return this.localNickname
			},
			set (val) {
				this.localNickname = val
			}
		},
		departmentClient: {
			get () {
				return this.editClientForm.departmentClient
			},
			set (val) {
				this.updateClientForm({ departmentClient: val })
			}
		},
		name: {
			get () {
				return this.localName
			},
			set (val) {
				this.localName = val
			}
		},
		surname: {
			get () {
				return this.localSurname
			},
			set (val) {
				this.localSurname = val
			}
		},
		email: {
			get () {
				return this.localEmail
			},
			set (val) {
				this.localEmail = val
			}
		},
		phoneNumber: {
			get () {
				return this.localPhoneNumber
			},
			set (val) {
				this.localPhoneNumber = val
			}
		},
		city: {
			get () {
				return this.localCity
			},
			set (val) {
				this.localCity = val
			}
		},
		address: {
			get () {
				return this.localAddress
			},
			set (val) {
				this.localAddress = val
			}
		},
		postalCode: {
			get () {
				return this.localPostalCode
			},
			set (val) {
				this.localPostalCode = val
			}
		}
	},

	watch: {
		'country.id': {
			handler (val) {
				this.updateClientForm({ country: val })
			}
		}
	},

	created () {
		this.$validator.pause()
		this.localNickname = this.editClientForm.nickname
		this.localName = this.editClientForm.name
		this.localSurname = this.editClientForm.surname
		this.localEmail = this.editClientForm.email
		this.localPhoneNumber = this.editClientForm.phoneNumber
		this.localCity = this.editClientForm.city || this.editClientForm.deliveryCity || null
		this.localAddress = this.editClientForm.address
		this.localPostalCode = this.editClientForm.postalCode

		if (this.editClientForm.country) {
			this.country = this.countries.find(country => country.id.toLowerCase() === this.editClientForm.country.toLowerCase())
		}
	},

	methods: {
		...mapMutations({
			updateClientForm: UPDATE_CLIENT_FORM
		}),
		async validate (locked = false) {
			this.updateClientForm({
				nickname: this.localNickname,
				name: this.localName,
				surname: this.localSurname,
				email: this.localEmail,
				phoneNumber: this.localPhoneNumber,
				city: this.localCity,
				address: this.localAddress,
				postalCode: this.localPostalCode
			})

			if (locked) {
				const hasIdentity = [this.localName, this.localSurname, this.localNickname]
					.some(value => value && String(value).trim())
				if (!hasIdentity) {
					this.$emit('shake')
					return false
				}
				return true
			}

			this.errors.remove('nickname')
			this.$validator.resume()
			const valid = await this.$validator.validateAll()

			if (!this.$refs.phoneNumber.isValid()) {
				this.errors.add({ field: 'phoneNumber', msg: this.$t('orders-view.inner.delivery-info.delivery-info-modal.errors.incorrect-phone-number') })
				this.$emit('shake')
				return false
			}
			this.errors.remove('phoneNumber')

			if (!valid) {
				this.$emit('shake')
			}
			return valid
		},

		raiseFieldError (field, message) {
			if (field && message) {
				this.errors.add({ field, msg: message })
			}
		},

		handlePlaceChange ({ _addressData, placeResultData }) {
			const country = getCountryComponentFromPlace(placeResultData)
			const city = getCityFromPlace(placeResultData)
			const postalCode = placeResultData.address_components?.find(c => c.types.includes('postal_code'))

			this.country = this.countries.find(c => c.id === country?.short_name) || {
				id: null,
				name: null
			}
			this.city = city
			this.postalCode = postalCode ? postalCode.short_name : null
		}
	}
}
</script>

<style lang="scss" module>
	.tab-wrapper {
		display: flex;
		flex-direction: column;
	}
</style>
